import { SignalObject, signalObject } from "omi";

import { NetworkSignal } from "../signals";

/**
 * Type for network state object containing NetworkSignal values
 */
type NetworkStateObject<T> = {
  [key: string]: NetworkSignal<T>;
};

/**
 * Represents the structure of a store configuration
 * @template TState The type of the store's state
 * @template TNetworkState Type extending NetworkStateObject for network state
 * @template TActions The type of the store's actions
 * @template TSlices The type of the store's slices
 */
export type StoreConfig<TState, TNetworkState extends NetworkStateObject<any>, TActions, TSlices> = {
  /** Initial state of the store */
  initialState: TState;
  /** Network state to be attached to the store */
  networkState: TNetworkState;
  /** Slices to be attached to the store */
  slices: (store: SignalObject<TState>) => TSlices;
  /** Actions to be attached to the store */
  actions: (options: { store: SignalObject<TState>; networkState: TNetworkState; slices: TSlices }) => TActions;
};

/**
 * Represents a store instance combining state and actions
 * @template TState The type of the store's state
 * @template TNetworkState Type extending NetworkStateObject for network state
 * @template TActions The type of the store's actions
 * @template TSlices The type of the store's slices
 */
export type Store<TState, TNetworkState extends NetworkStateObject<any>, TActions, TSlices> = SignalObject<TState> &
  TNetworkState &
  TActions &
  TSlices;

/**
 * Creates a store with state management and actions
 * @template TState The type of the store's state
 * @template TNetworkState Type extending NetworkStateObject for network state
 * @template TActions The type of the store's actions
 * @template TSlices The type of the store's slices
 * @param config Configuration object containing initial state and action creators
 * @returns A store instance combining state and actions
 */
export function createStore<
  TState extends object,
  TNetworkState extends NetworkStateObject<any>,
  TActions extends object,
  TSlices extends object = {},
>(config: StoreConfig<TState, TNetworkState, TActions, TSlices>): Store<TState, TNetworkState, TActions, TSlices> {
  const store = signalObject(config.initialState);
  const networkState = config.networkState;
  const slices = config.slices(store);
  const actions = config.actions({ store, networkState, slices });

  return {
    ...store,
    ...networkState,
    ...actions,
    ...slices,
  };
}
